import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Volume2, VolumeX, Play, Lightbulb, Loader2, Repeat, ArrowLeft, Maximize2, Minimize2, Rocket, Unlock, Trophy } from "lucide-react";
import { LiveZoya } from "@/components/LiveZoya";
import { ARM_START, BASKET, BLOCK_HOME, ARM, LIMITS, armTip, dist, overBasket } from "@/lib/arm";
import { useLang } from "@/context/LanguageContext";
import { clip } from "@/lib/zoya";
import { fetchLabs, getLocalLabs, saveLab, useClassLang, useClassPrefs } from "@/lib/smart";
import { LAB_META, fmtTime, nextUnlock, unlockedFrom } from "@/lib/labs";
import { useFullscreen } from "@/lib/useFullscreen";
import { PartDock } from "@/components/smart/lab/PartDock";
import { Check, PropsConfig, RuleConfig, Reflection } from "@/components/smart/lab/BuildSteps";
import { ArmControls } from "@/components/smart/lab/ArmControls";
import { SkillMap } from "@/components/smart/lab/SkillMap";
import { MissionHost, Stars } from "@/components/smart/lab/MissionHost";

const BuildScene = lazy(() => import("@/components/lab3d/BuildScene"));
const IDLE_MS = 25000; // no action on a step for this long = stuck, so Zoya gives a hint
const DURATION = { linefollower: 5000 };
const shuffled = (arr, seed) => arr.map((x, i) => [x, Math.sin(seed * 97 + i * 13) * 1e4 % 1]).sort((a, b) => a[1] - b[1]).map((x) => x[0]);
const ARM_INIT = { ...ARM_START, grip: "open", held: false, block: BLOCK_HOME };
const NOTES = {
  oops: ["Not this part yet. Listen to Zoya Ma'am again.", "अभी ये part नहीं। ज़ोया मैम को फिर से सुनिए।", "#C81E4B"],
  aim: ["Right part! Drop it on the glowing ring in the 3D view.", "सही part! इसे 3D view में चमकती ring पर छोड़िए।", "#B45309"],
  snap: ["Perfect fit!", "बिल्कुल सही जगह!", "#047857"],
  missed: ["Missed! Open the gripper, move closer to the block and close it again.", "चूक गए! Gripper खोलिए, block के और पास ले जाइए और फिर बंद कीजिए।", "#C81E4B"],
};

const FsBtn = ({ full, onClick }) => {
  const { L } = useLang();
  return (
    <button onClick={onClick} aria-label={full ? L("Exit fullscreen", "फ़ुलस्क्रीन बंद") : L("Fullscreen", "फ़ुलस्क्रीन")} title={full ? "Esc" : L("Fullscreen", "फ़ुलस्क्रीन")}
      data-testid="lab-fullscreen-btn" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full grid place-items-center bg-black/50 backdrop-blur border border-white/20 text-white hover:bg-[#00E5FF] hover:text-[#0A192F] transition-colors">
      {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
    </button>
  );
};

const MissionCta = ({ lab, best, onStart }) => {
  const { L } = useLang();
  const meta = LAB_META[lab.id];
  return (
    <div className="rounded-3xl bg-[#0A192F] p-4 text-white" data-testid="lab-mission-cta">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">{L("Mission unlocked", "मिशन खुला")}</div>
      <div className="mt-1 text-lg font-extrabold">{L(...meta.mission)}</div>
      <p className="text-xs text-slate-300">{L(...meta.goal)}</p>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-300"><Stars n={best?.mission_stars || 0} size="w-4 h-4" /><span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-[#FFC94D]" />{fmtTime(best?.mission_best ?? null)}</span></div>
      <button onClick={onStart} data-testid="lab-mission-btn" className="mt-3 w-full h-12 rounded-full bg-[#00E5FF] text-[#0A192F] font-extrabold inline-flex items-center justify-center gap-2 hover:bg-white transition-colors"><Rocket className="w-5 h-5" />{L("Play the mission", "मिशन खेलें")}</button>
    </div>
  );
};

const Workbench = ({ lab, labs, propsAnswer, ruleAnswer, audio, best, onBack, onProgress, onRestart }) => {
  const { L } = useLang();
  const lang = useClassLang();
  const lowbw = useClassPrefs((s) => s.lowbw);
  const [si, setSi] = useState(0);
  const [sub, setSub] = useState("part");
  const [phase, setPhase] = useState("build");
  const [placed, setPlaced] = useState([]);
  const [mistakes, setMistakes] = useState(0);
  const [checksOk, setChecksOk] = useState(0);
  const [wrong, setWrong] = useState(null);
  const [props, setProps] = useState(["cw", "cw", "cw", "cw"]);
  const [test, setTest] = useState("idle");
  const [hint, setHint] = useState(null);
  const [note, setNote] = useState(null);
  const [shake, setShake] = useState(null);
  const [voice, setVoice] = useState(true);
  const [rule, setRule] = useState(null);
  const [arm, setArm] = useState(ARM_INIT);
  const [armTipHint, setArmTipHint] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [mission, setMission] = useState(false);
  const [unlocked, setUnlocked] = useState(null);
  const voiceRef = useRef(true);
  const wrap = useRef(null);
  const anchors = useRef({});
  const root = useRef(null);
  const { full, css, toggle } = useFullscreen(root);
  const parts = useMemo(() => shuffled(lab.steps.map((s) => s.part), lab.id.length), [lab.id, lab.steps]);
  const step = lab.steps[si];
  const checksTotal = lab.steps.filter((s) => s.check).length;
  const meta = LAB_META[lab.id];
  const speak = (text) => { if (voiceRef.current) audio.say(text, lang); };

  useEffect(() => { speak(lab.steps[0].say); return () => audio.stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase !== "build" || sub !== "part") return undefined;
    const t = setTimeout(() => { setHint(step.part); setNote("hint"); speak(step.say); }, IDLE_MS);
    return () => clearTimeout(t);
  }, [si, sub, phase, mistakes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lab.id !== "arm" || phase !== "test") return undefined;
    const t = setTimeout(() => setArmTipHint(true), IDLE_MS);
    return () => clearTimeout(t);
  }, [lab.id, phase, arm]);

  const advance = () => {
    setWrong(null); setHint(null);
    if (si + 1 < lab.steps.length) { setSi(si + 1); setSub("part"); speak(lab.steps[si + 1].say); }
    else { setPhase("test"); speak(lab.test); }
  };
  const pickPart = (p) => {
    if (p !== step.part) {
      setMistakes((m) => m + 1); setNote("oops"); setShake(p); setTimeout(() => setShake(null), 450);
      if (voiceRef.current) audio.play(clip("wrong"), () => speak(step.say));
      return false;
    }
    setPlaced((x) => [...x, p]); setHint(null); setNote(null);
    if (step.check) { setSub("check"); speak(step.check.q); } else if (step.props) setSub("props");
    else if (step.rule) { setSub("rule"); speak(lab.rule_q); } else advance();
    return true;
  };
  const dropPart = (p, x, y) => {
    const r = wrap.current?.getBoundingClientRect();
    if (!r || x < r.left || x > r.right || y < r.top || y > r.bottom) return;
    if (p !== step.part) { pickPart(p); return; }
    const a = anchors.current?.[p];
    if (a && Math.hypot(x - r.left - a.x, y - r.top - a.y) > Math.max(70, r.width * 0.1)) { setNote("aim"); return; }
    if (pickPart(p)) setNote("snap");
  };
  const answer = (k) => {
    if (k === step.check.answer) { if (wrong === null) setChecksOk((n) => n + 1); if (voiceRef.current) audio.play(clip("correct")); advance(); return; }
    setWrong(k); setMistakes((m) => m + 1); if (voiceRef.current) audio.play(clip("wrong"));
  };
  const finish = async (ok) => {
    if (ok) setUnlocked(nextUnlock(labs, lab.id) || null);
    try { await saveLab(lab.id, { success: ok, mistakes, checks_ok: checksOk, checks_total: checksTotal, config: lab.id === "drone" ? { props: props.join(",") } : lab.id === "linefollower" ? { rule } : { parts: placed.length } }); }
    finally { onProgress?.(); }
  };
  const setJoint = (k, v) => { setArm((a) => ({ ...a, [k]: Math.max(LIMITS[k][0], Math.min(LIMITS[k][1], Math.round(v))) })); setArmTipHint(false); };
  const grip = () => {
    const tip = armTip(arm);
    if (arm.grip === "open") {
      if (dist(tip, arm.block) < ARM.grab) { setArm({ ...arm, grip: "closed", held: true }); if (voiceRef.current) audio.play(clip("correct")); }
      else { setArm({ ...arm, grip: "closed" }); setMistakes((m) => m + 1); setNote("missed"); }
      return;
    }
    if (!arm.held) { setArm({ ...arm, grip: "open" }); setNote(null); return; }
    if (overBasket(tip)) {
      setArm({ ...arm, grip: "open", held: false, block: { x: BASKET.x, y: 0.12, z: BASKET.z } });
      setPhase("done"); setNote(null); speak(lab.success); finish(true);
    } else {
      setArm({ ...arm, grip: "open", held: false, block: { x: tip.x, y: 0.09, z: tip.z } });
      setMistakes((m) => m + 1); setNote("dropped"); speak(lab.fail);
    }
  };
  const runTest = () => {
    const ok = lab.id === "linefollower" ? rule === ruleAnswer : lab.id === "drone" ? props.every((x, i) => x === propsAnswer[i]) : true;
    setTest(ok ? "run-ok" : "run-fail"); setPhase("testing"); audio.stop();
    setTimeout(() => { setTest(ok ? "ok" : "fail"); setPhase(ok ? "done" : "fail"); speak(ok ? lab.success : lab.fail); finish(ok); }, DURATION[lab.id] || 3200);
  };
  const toggleVoice = () => { voiceRef.current = !voice; setVoice(!voice); if (voice) audio.stop(); };
  const sceneFallback = <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-slate-300" data-testid="lab-no-webgl">{L("3D needs WebGL. Your parts list still works: keep building!", "3D के लिए WebGL चाहिए। Parts list से बनाना जारी रखिए!")}</div>;
  const loader = <div className="absolute inset-0 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#00E5FF]" /></div>;

  const grid = (scene, side) => (
    <div className={`grid gap-4 lg:gap-5 ${full ? "lg:grid-cols-[1fr_400px]" : "lg:grid-cols-[1fr_380px]"}`}>
      <div className="min-w-0">
        <div ref={wrap} className={`relative rounded-3xl overflow-hidden border border-[#16335C] bg-[#050A16] touch-none shadow-[0_30px_90px_-30px_rgba(0,184,212,0.45)] ${full ? "h-[58vh] lg:h-[calc(100vh-2rem)]" : "aspect-[4/3] sm:aspect-[16/10]"}`} data-testid="lab-canvas-wrap">
          {scene}
          <FsBtn full={full} onClick={toggle} />
        </div>
      </div>
      <aside className="space-y-3 min-w-0">{side}</aside>
    </div>
  );

  const buildScene = (
    <>
      <Suspense fallback={loader}>
        <BuildScene lab={lab.id} placed={placed} hint={hint} target={dragging ? step?.part : null} props={props} test={test} arm={arm} lowbw={lowbw} fallback={sceneFallback} anchorsOut={phase === "build" ? anchors : null} />
      </Suspense>
      <div className="absolute top-3 left-3 flex gap-1.5" data-testid="lab-step-dots">
        {lab.steps.map((s, i) => <span key={i} className={`w-2.5 h-2.5 rounded-full ${placed.includes(s.part) ? "bg-emerald-400 shadow-[0_0_8px_#34D399]" : i === si && phase === "build" ? "bg-[#FF2E63] shadow-[0_0_8px_#FF2E63]" : "bg-white/20 border border-white/30"}`} />)}
      </div>
      <div className="absolute top-3 right-14 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-[11px] font-bold text-white" data-testid="lab-mistakes">{L(`Mistakes: ${mistakes}`, `ग़लतियाँ: ${mistakes}`)}</div>
      {dragging && <div className="absolute inset-x-0 bottom-12 text-center pointer-events-none"><span className="px-3 py-1.5 rounded-full bg-[#00E5FF] text-[#0A192F] text-xs font-extrabold" data-testid="lab-drop-hint">{L("Drop it on the glowing ring", "चमकती ring पर छोड़िए")}</span></div>}
      <div className="absolute bottom-3 left-3 text-[11px] text-slate-300 bg-black/40 backdrop-blur rounded-full px-2.5 py-1">{L("Drag to rotate · pinch to zoom", "घुमाने के लिए drag · zoom के लिए pinch")}</div>
    </>
  );
  const noteRow = note && NOTES[note] ? <p className="text-xs font-semibold px-1" style={{ color: NOTES[note][2] }} data-testid={note === "missed" ? "lab-arm-missed" : `lab-${note}`}>{L(NOTES[note][0], NOTES[note][1])}</p> : null;
  const buildSide = (
    <>
      <div className="flex items-center gap-3 rounded-3xl bg-white border border-slate-200 p-3">
        <LiveZoya src={meta.teacher} speaking={audio.speaking} levelRef={audio.levelRef} className="w-20 shrink-0 rounded-2xl" testid="lab-zoya" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#007A8A]" data-testid="lab-step-count">
            {phase === "build" ? L(`Step ${si + 1} of ${lab.steps.length}`, `चरण ${si + 1} / ${lab.steps.length}`) : phase === "done" ? L("Lab complete", "लैब पूरी") : L("Test time", "टेस्ट का समय")}</div>
          <p className="text-sm font-semibold text-[#0A192F] leading-snug" data-testid="lab-step-text">
            {phase === "build" ? step.say : phase === "done" ? lab.success : phase === "fail" ? lab.fail : lab.test}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={toggleVoice} aria-label="Voice guide" data-testid="lab-voice-toggle" className={`w-9 h-9 rounded-full grid place-items-center border ${voice ? "bg-[#00E5FF]/20 border-[#00B8D4] text-[#00707F]" : "border-slate-300 text-slate-500"}`}>{voice ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
          <button onClick={() => speak(phase === "build" ? (sub === "check" ? step.check.q : step.say) : lab.test)} aria-label="Repeat" data-testid="lab-repeat-btn" className="w-9 h-9 rounded-full grid place-items-center border border-slate-300"><Repeat className="w-4 h-4" /></button>
        </div>
      </div>
      {!lab.lang_ready && <p className="text-[11px] font-semibold text-[#B45309] px-1" data-testid="lab-english-note">{L("This project is in English for now. More languages are coming soon.", "यह प्रोजेक्ट अभी English में है। बाकी भाषाएँ जल्द आएँगी।")}</p>}
      {noteRow}
      {note === "dropped" && <p className="text-xs font-semibold text-[#C81E4B] px-1" data-testid="lab-arm-dropped">{lab.fail}</p>}
      {note === "hint" && <p className="text-xs font-semibold text-[#007A8A] px-1 inline-flex items-center gap-1" data-testid="lab-hint"><Lightbulb className="w-4 h-4" />{L("Stuck? The glowing part is the one you need.", "अटक गए? चमकता हुआ part ही चाहिए।")}</p>}
      {phase === "build" && sub === "check" && <Check check={step.check} wrong={wrong} onAnswer={answer} />}
      {phase === "build" && sub === "props" && <PropsConfig props={props} setProps={setProps} onDone={advance} />}
      {phase === "build" && sub === "rule" && <RuleConfig q={lab.rule_q} rule={rule} setRule={setRule} onDone={advance} />}
      {phase === "build" && sub !== "check" && (
        <>
          <p className="text-[11px] text-slate-500 px-1">{L("Drag a part onto the glowing ring in 3D, or tap it.", "Part को 3D में चमकती ring पर drag कीजिए, या tap कीजिए।")}</p>
          <PartDock labId={lab.id} parts={parts} placed={placed} need={step.part} shake={shake} hint={hint} disabled={sub !== "part"} onPick={pickPart} onDrop={dropPart} onDrag={setDragging} />
        </>
      )}
      {(phase === "test" || phase === "fail") && lab.id === "drone" && <PropsConfig props={props} setProps={setProps} />}
      {(phase === "test" || phase === "fail") && lab.id === "linefollower" && <RuleConfig q={lab.rule_q} rule={rule} setRule={setRule} />}
      {phase === "test" && lab.id === "arm" && <ArmControls arm={arm} setJoint={setJoint} onGrip={grip} tip={armTipHint} held={arm.held}
        near={!arm.held && dist(armTip(arm), arm.block) < ARM.grab} over={arm.held && overBasket(armTip(arm))} onReset={() => { setArm(ARM_INIT); setNote(null); }} />}
      {(phase === "test" || phase === "fail") && lab.id !== "arm" && (
        <button onClick={runTest} data-testid="lab-test-btn" className="w-full h-12 rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white font-extrabold inline-flex items-center justify-center gap-2"><Play className="w-5 h-5" />{phase === "fail" ? L("Fix it and test again", "ठीक करके फिर टेस्ट करें") : L("Test", "टेस्ट करें")}</button>
      )}
      {phase === "testing" && <div className="h-12 rounded-full bg-white border border-slate-200 grid place-items-center text-sm font-bold text-[#007A8A]" data-testid="lab-testing"><span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{L("Testing…", "टेस्ट हो रहा है…")}</span></div>}
      {phase === "fail" && <p className="text-xs font-semibold text-[#C81E4B] px-1" data-testid="lab-result-fail">{lab.id === "linefollower" ? L("Test failed. Check the steering rule.", "टेस्ट फ़ेल। Steering rule जाँचिए।") : L("Test failed. Check the propeller directions.", "टेस्ट फ़ेल। Propeller की दिशा जाँचिए।")}</p>}
      {phase === "done" && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-sm font-bold text-emerald-800 inline-flex items-center gap-2 w-full" data-testid="lab-result-success"><CheckCircle2 className="w-5 h-5 shrink-0" />{L(`Lab complete! Badge earned: ${meta.badge[0]} · checkpoints ${checksOk}/${checksTotal} · mistakes ${mistakes}`, `लैब पूरी! बैज मिला: ${meta.badge[1]} · चेकपॉइंट ${checksOk}/${checksTotal} · ग़लतियाँ ${mistakes}`)}</div>}
      {phase === "done" && unlocked && <div className="rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#FF2E63] p-3 text-sm font-bold text-white inline-flex items-center gap-2 w-full" data-testid="lab-unlocked-next"><Unlock className="w-5 h-5 shrink-0" />{L(`New project unlocked: ${unlocked.title}`, `नया प्रोजेक्ट खुला: ${unlocked.title}`)}</div>}
      {phase === "done" && <MissionCta lab={lab} best={best} onStart={() => { audio.stop(); setMission(true); }} />}
      {phase === "done" && <Reflection labId={lab.id} onRestart={onRestart} />}
      {phase === "build" && best?.done && <button onClick={() => { audio.stop(); setMission(true); }} data-testid="lab-skip-to-mission-btn" className={`w-full h-11 rounded-full border-2 text-sm font-bold inline-flex items-center justify-center gap-2 ${full ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#0A192F] text-[#0A192F]"}`}><Rocket className="w-4 h-4" />{L("Already built: jump to the mission", "पहले बन चुका: सीधे मिशन खेलें")}</button>}
      <button onClick={onBack} data-testid="lab-back-btn" className={`inline-flex items-center gap-1.5 text-sm font-semibold ${full ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#0A192F]"}`}><ArrowLeft className="w-4 h-4" />{L("Skill Map", "स्किल मैप")}</button>
    </>
  );

  return (
    <div ref={root} data-testid="lab-workbench" data-phase={mission ? "mission" : phase} data-step={si} data-full={full ? "1" : "0"}
      className={full ? `bg-[#050A16] p-3 sm:p-4 overflow-y-auto ${css ? "fixed inset-0 z-[120]" : "h-full w-full"}` : ""}>
      {mission ? <MissionHost lab={lab} parts={lab.steps.map((s) => s.part)} best={best} lowbw={lowbw} fallback={sceneFallback} layout={grid}
        onExit={() => setMission(false)} onSaved={() => { if (voiceRef.current) audio.play(clip("correct")); onProgress?.(); }} /> : grid(buildScene, buildSide)}
    </div>
  );
};

export const RobotLab = ({ audio, initial, profile, onProgress }) => {
  const { L } = useLang();
  const lang = useClassLang();
  const [data, setData] = useState(null);
  const [labId, setLabId] = useState(null);
  const [locked, setLocked] = useState(null);
  const [run, setRun] = useState(0);
  const handled = useRef(null);
  useEffect(() => { fetchLabs(lang).then(setData).catch(() => setData(false)); }, [lang]);
  const progress = profile?.labs || getLocalLabs();
  const unlocked = data ? profile?.unlocked || unlockedFrom(data.labs, progress) : [];
  const open = (id) => {
    if (!unlocked.includes(id)) { setLocked(id); setLabId(null); return; }
    setLocked(null); setLabId(id);
  };
  useEffect(() => {
    if (!initial?.id || !data || profile === null || handled.current === initial.at) return;
    handled.current = initial.at;
    open(initial.id);
  }, [initial, data, profile]); // eslint-disable-line react-hooks/exhaustive-deps
  if (data === false) return <p className="text-sm text-[#C81E4B]" data-testid="lab-error">{L("Couldn't load the labs. Check your internet and try again.", "लैब लोड नहीं हुई। इंटरनेट देखकर फिर कोशिश करें।")}</p>;
  if (!data) return <div className="h-64 rounded-3xl bg-white/70 animate-pulse" data-testid="lab-loading" />;
  return (
    <div data-testid="robot-lab">
      {!data.translated && <p className="mb-3 text-xs text-[#B45309]" data-testid="lab-lang-fallback">{L("This language isn't ready for the lab yet, so it's in English for now.", "यह भाषा अभी लैब के लिए तैयार नहीं है, इसलिए अभी English में है।")}</p>}
      {labId ? <Workbench key={`${labId}-${run}`} lab={data.labs[labId]} labs={data.labs} best={progress[labId]} propsAnswer={data.props_answer} ruleAnswer={data.rule_answer} audio={audio}
        onProgress={onProgress} onBack={() => { audio.stop(); setLabId(null); }} onRestart={() => setRun((n) => n + 1)} />
        : <SkillMap labs={data.labs} progress={progress} unlocked={unlocked} locked={locked} onPick={open} />}
    </div>
  );
};
